import { BoundCheckboxField, Css, IconButton, ModalBody, Palette, useModal, useTestIds } from "@homebound/beam";
import { FieldState } from "@homebound/form-state";
import { Observer } from "mobx-react";

type PackageDisplayOptionFieldProps = { field: FieldState<boolean | null | undefined> };

/**
 * The "Display option as combined packages in Checkout" toggle for Package-type personalization options,
 * with an info icon that opens a modal showing separated-vs-combined package examples.
 *
 * Used by both the legacy `EditPersonalizationOptionPage` and the new `EditPersonalizationOptionWizard`.
 */
export function PackageDisplayOptionField(props: PackageDisplayOptionFieldProps) {
  const { field } = props;
  const { openModal } = useModal();
  const tid = useTestIds({});

  return (
    <Observer>
      {() => (
        <div css={Css.df.aic.gap1.$}>
          <BoundCheckboxField field={field} label="Display option as combined packages in Checkout" fullWidth />
          <IconButton
            icon="infoCircle"
            inc={2}
            color={Palette.Gray500}
            label="See an example of separated vs. combined packages"
            onClick={() => openModal({ content: <CombinedPackagesPreviewModal /> })}
            {...tid.combinedPackagesInfo}
          />
        </div>
      )}
    </Observer>
  );
}

function CombinedPackagesPreviewModal() {
  const tid = useTestIds({});
  return (
    <>
      <ModalBody>
        {/* Large bottom padding to act like a footer without cutting off the img box shadow */}
        <div css={Css.df.gap4.jcc.pb6.$}>
          <div css={Css.df.fdc.gap2.aifs.$}>
            <span css={Css.smSb.gray900.$}>Separated packages</span>
            <img
              css={Css.br8.boxShadow("0px 2.57px 11.14px rgba(0,0,0,0.12)").$}
              src="/images/separated-packages-preview.svg"
              alt="Example of a package option displayed as separated packages"
              {...tid.separatedPackagesPreview}
            />
          </div>
          <div css={Css.df.fdc.gap2.aifs.$}>
            <span css={Css.smSb.gray900.$}>Combined packages</span>
            <img
              css={Css.br8.boxShadow("0px 2.57px 11.14px rgba(0,0,0,0.12)").$}
              src="/images/combined-packages-preview.svg"
              alt="Example of a package option displayed as combined packages"
              {...tid.combinedPackagesPreview}
            />
          </div>
        </div>
      </ModalBody>
    </>
  );
}
